import {
  getDateFromNric,
  getGenderFromNric,
  isBelowAnb,
  pickNricFieldFromError,
} from '@/utils/applicant';
import { formatRequestDate, getAgeInYearFromDate } from '@/utils/date';
import { getPreferredLang } from '@/utils/preferredLang';
import { useProductCode } from '@/utils/product';
import { isValidNric } from '@/utils/validators';
import { yupResolver } from '@hookform/resolvers/yup';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostQuickQuotationMutation } from '../apis';
import { MAX_ANB, ProductLabel } from '../constants';
import {
  Answer,
  ClientType,
  ErrorCode,
  ErrorCodePrefix,
  ErrorMessage,
  Gender,
  IdType,
  Product,
  Relationship,
} from '../enums';
import { useAppDispatch, useAppSelector } from '../reducers';
import {
  updateFormValues,
  updatePlans,
  updatePremiums,
  updateUwRemarks,
} from '../reducers/application';
import { quotationSchema } from '../schemas';
import { ApiResponse } from '../types/api';
import { IsError } from '../types/component';
import { QuotationFormValues } from '../types/form';
import { Applicant } from '../types/model';
import { useRoute } from './useRoute';
import { useOptions } from './useOptions';
import { pushDataLayer } from '@/utils/googleAnalytics';
import { useGAFactory } from './useGAFactory';

export const useQuotation = () => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const application = useAppSelector((state) => state.application);
  const { redirect, product } = useRoute();
  const productCode = useProductCode();
  const isEasyHealth = product === Product.EasyHealth;
  const [isContactDetailsOpen, setIsContactDetailsOpen] = useState(false);

  const GAFactory = useGAFactory();
  const [isError, setIsError] = useState<IsError>({
    isErrorAge: false,
    isErrorOccupation: false,
  });
  const [isApeDecline, setIsApeDecline] = useState<boolean>(false);
  const { getProtections } = useOptions();

  const formMethods = useForm<QuotationFormValues>({
    resolver: yupResolver(quotationSchema),
    defaultValues: application.formValues?.quotation,
  });
  const { control, setValue, watch } = formMethods;

  const [postQuickQuote, { isLoading }] = usePostQuickQuotationMutation();

  const onCloseContactDetailsForm = () => {
    setIsContactDetailsOpen(false);
  };

  const onCloseApeDialog = () => {
    setIsApeDecline(false);
  };

  const isKid = (relationship: Relationship) => {
    return [Relationship.Son, Relationship.Daughter].includes(relationship);
  };

  const isBelow = (nric: string, relationship: Relationship) => {
    if (!nric) return true;
    if (!isValidNric(nric)) return false;

    return isKid(relationship) && isBelowAnb(nric);
  };

  const getRelationship = (formValues: QuotationFormValues): Relationship => {
    // get policy owner relationship
    if (
      formValues.life_insured.gender === Gender.MALE &&
      formValues.life_insured.relationship === Relationship.Spouse
    ) {
      return Relationship.Husband;
    } else if (
      formValues.life_insured.gender === Gender.FEMALE &&
      formValues.life_insured.relationship === Relationship.Spouse
    ) {
      return Relationship.Wife;
    } else {
      return Relationship.Self;
    }
  };

  const getApplicantsInfo = (formValues: QuotationFormValues): Applicant[] => {
    var { life_insured: lifeInsured, policy_owner: policyOwner } = formValues;
    const result: Applicant[] = [];

    const createApplicant = (
      clientType: ClientType,
      person: {
        gender?: string;
        name?: string;
        email?: string;
        nric?: string;
        relationship?: string;
        occupation?: string;
      },
      relationshipOverride?: Relationship,
    ): Applicant =>
      ({
        clientType,
        gender: person.gender as Gender,
        name: { fullName: person.name as string },
        emails: [
          {
            type: 'email',
            value: person.email as string,
            isPrimary: Answer.Yes,
          },
        ],
        idType: IdType.IC,
        idNo: person.nric as string,
        relationship: (relationshipOverride ??
          person.relationship) as Relationship,
        dob: formatRequestDate(getDateFromNric(person.nric as string)),
        occupationCode: person.occupation,
        deductibleAmount: application.deductible,
      }) as Applicant;

    if (lifeInsured?.relationship === Relationship.Self) {
      result.push(
        createApplicant(ClientType.ProposerOrLifeAssured, lifeInsured),
      );
    } else {
      if (policyOwner) {
        policyOwner = {
          ...policyOwner,
          gender: lifeInsured.gender as Gender,
        };
        result.push(
          createApplicant(
            ClientType.Proposer,
            policyOwner,
            lifeInsured.relationship !== Relationship.Spouse
              ? policyOwner.gender === Gender.MALE
                ? Relationship.Father
                : Relationship.Mother
              : Relationship.Spouse,
          ),
        );
      }
      if (lifeInsured) {
        lifeInsured = {
          ...lifeInsured,
          gender: getGenderFromNric(lifeInsured.nric ?? ''),
        };
        result.push(
          createApplicant(
            ClientType.LifeAssured,
            lifeInsured,
            Relationship.Self,
          ),
        );
      }
    }

    return result.map((applicant, index) => ({
      ...applicant,
      id: index + 1,
    }));
  };

  const [nric, relationship] = useWatch({
    control,
    name: ['life_insured.nric', 'life_insured.relationship'],
  });

  useEffect(() => {
    if (nric !== '') {
      getOccupationForKid();
    }
  }, [nric, relationship]);

  const getOccupationForKid = () => {
    // Only apply the kid-occupation logic when the insured is a Son or
    // Daughter. For Self/Spouse we must NOT touch the occupation field,
    // otherwise transitioning Son/Daughter → Self via the "Proceed Now"
    // CTA would clobber the value we just pre-filled from policy_owner.
    if (!isKid(relationship as Relationship)) return undefined;

    if (
      nric !== undefined &&
      isBelow(nric ?? '', relationship as Relationship)
    ) {
      setValue('life_insured.occupation', 'JUV001');
    } else
      setValue(
        'life_insured.occupation',
        application.formValues.quotation?.life_insured?.occupation,
      );
    return undefined;
  };

  const onSubmit = async (formValues: QuotationFormValues) => {
    pushDataLayer(
      GAFactory('health_begin_quotation', { quotaionsValue: formValues }),
    );
    dispatch(
      updateFormValues({
        quotation: formValues,
      }),
    );
    const protectionList = getProtections(
      formValues.life_insured.gender as Gender,
    );
    const insecuredPerson = protectionList.filter(
      (item) => formValues.life_insured.relationship === item.value,
    );

    try {
      const result = await postQuickQuote({
        appId: application.appId,
        distributionChannel: application.distributionChannel,
        paymentMode: application.paymentMode,
        preferredLang: getPreferredLang(i18n.language),
        productCode,
        applicants: getApplicantsInfo(formValues),
      }).unwrap();
      const uwRemarks = result.data.applicants?.[0].uwRemarks ?? '';
      if (
        result.data.applicants.find(
          (applicant) => applicant.uwResultCode === ErrorCode.Ape_Decline,
        )
      ) {
        setIsApeDecline(true);
        formMethods.reset(formValues, { keepErrors: true });
      } else if (
        result.data.applicants.find(
          (applicant) => applicant.uwResult === 'Declined',
        )
      ) {
        dispatch(updateUwRemarks(uwRemarks));
        setIsContactDetailsOpen(true);
        setIsError({ isErrorOccupation: true });
        formMethods.reset(formValues, { keepErrors: true });
      } else {
        dispatch(updatePlans(result.data.planPremium));
        dispatch(
          updatePremiums({
            monthlyTotalPremium: result.data.monthlyTotalPremium,
            yearlyTotalPremium: result.data.yearlyTotalPremium,
            annualTotalPremium: result.data.annualTotalPremium,
          }),
        );
        redirect('plan');
      }
    } catch (error) {
      const errorResponse = error as FetchBaseQueryError;
      const data = errorResponse.data as ApiResponse<unknown>;

      const isValidationError = [
        ErrorCodePrefix.LVE,
        ErrorCodePrefix.IVE,
        ErrorCodePrefix.DVE,
      ].some((err) => err === data.errorCode?.substring(0, 3));
      dispatch(updateUwRemarks(data.errorMsg));

      setIsContactDetailsOpen(true);

      if (!isValidationError) {
        redirect('error');
        return;
      }

      const isHitErrorAge = [ErrorCode.ErrorAgeMin, ErrorCode.ErrorAgeMax].some(
        (err) => err === data.errorCode,
      );

      const isDangerousOccupation =
        data.errorCode === ErrorCode.ErrorDangerousOccupation;

      setIsError({
        isErrorAge: isHitErrorAge,
        isErrorOccupation: isDangerousOccupation || !isHitErrorAge,
      });

      // after submit, form will be disabled
      // user will not be able to update fields' value
      // reset, so form will be enabled again
      formMethods.reset(formValues, { keepErrors: true });
      const targetField = !formValues?.policy_owner
        ? 'life_insured.nric'
        : pickNricFieldFromError(data.errorMsg);
      formMethods.setError(targetField, { message: ErrorMessage.NRIC });
    }
  };

  return {
    formMethods,
    onSubmit,
    isLoading,
    isEasyHealth,
    isContactDetailsOpen,
    isError,
    onCloseContactDetailsForm,
    isKid,
    isBelow,
    getOccupationForKid,
    isApeDecline,
    onCloseApeDialog,
    getApplicantsInfo,
  };
};
